import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert, WifiOff, Wifi, ShieldCheck, RotateCw, Cpu } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';

interface Alert {
  id: number;
  tenant_id: number;
  kind: string;
  at: string;
  source: string | null;
  details: string | Record<string, unknown>;
}

const KIND_LABELS: Record<string, { label: string; color: string; icon: any }> = {
  'endpoint.unreachable':        { label: 'Endpoint unreachable',   color: 'text-danger',  icon: WifiOff },
  'endpoint.recovered':          { label: 'Endpoint recovered',     color: 'text-success', icon: Wifi },
  'endpoint.ssl_expired':        { label: 'SSL expiré',             color: 'text-danger',  icon: ShieldAlert },
  'endpoint.ssl_expiring_soon':  { label: 'SSL expire bientôt',     color: 'text-warning', icon: ShieldAlert },
  'endpoint.promoted_to_managed':{ label: 'Promoted to TR-069',     color: 'text-brand',   icon: Cpu },
  'alarm.raised':                { label: 'Alarme déclenchée',      color: 'text-danger',  icon: AlertTriangle },
  'alarm.cleared':               { label: 'Alarme effacée',         color: 'text-success', icon: ShieldCheck },
};

const FILTER_TABS: Array<{ key: string; label: string; kinds: string[] | null }> = [
  { key: 'all',         label: 'Tous',           kinds: null },
  { key: 'reach',       label: 'Reachability',   kinds: ['endpoint.unreachable', 'endpoint.recovered'] },
  { key: 'ssl',         label: 'SSL',            kinds: ['endpoint.ssl_expired', 'endpoint.ssl_expiring_soon'] },
  { key: 'alarms',      label: 'Alarmes OLT',    kinds: ['alarm.raised', 'alarm.cleared'] },
  { key: 'lifecycle',   label: 'Lifecycle',      kinds: ['endpoint.promoted_to_managed'] },
];

export default function Alerts() {
  const [tab, setTab] = useState('all');
  const [hours, setHours] = useState(168);  // 7j default

  const tabDef = FILTER_TABS.find((t) => t.key === tab) ?? FILTER_TABS[0];

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', tab, hours],
    queryFn: () => api.get<{ data: Alert[] }>('/alerts', {
      params: { hours, kinds: tabDef.kinds?.join(',') },
    }).then((r) => r.data.data),
    refetchInterval: 30000,
  });

  const rows = data ?? [];
  const groupedByKind: Record<string, number> = {};
  rows.forEach((a) => { groupedByKind[a.kind] = (groupedByKind[a.kind] ?? 0) + 1; });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Alertes"
        subtitle={`${rows.length} events sur les ${hours <= 24 ? `${hours}h` : `${Math.round(hours / 24)}j`} dernier(s)`}
        actions={
          <div className="flex items-center gap-1 bg-bg-2 border border-border rounded text-xs">
            {[{ h: 24, l: '24h' }, { h: 168, l: '7j' }, { h: 720, l: '30j' }].map((o) => (
              <button key={o.h} onClick={() => setHours(o.h)}
                className={`h-7 px-3 ${hours === o.h ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}>{o.l}</button>
            ))}
          </div>
        }
      />

      {/* Tabs par type */}
      <div className="px-4 pt-2 border-b border-border bg-bg-2 flex items-center gap-1 flex-wrap">
        {FILTER_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`h-7 px-3 text-xs rounded-t border-b-2 ${tab === t.key ? 'border-brand text-brand bg-bg' : 'border-transparent text-text-muted hover:text-text'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Résumé compté par kind */}
      <div className="px-4 py-2 border-b border-border bg-bg-2 flex items-center gap-3 text-[11px] flex-wrap">
        {Object.entries(groupedByKind).map(([k, c]) => {
          const meta = KIND_LABELS[k] ?? { label: k, color: 'text-text-muted', icon: AlertTriangle };
          const Icon = meta.icon;
          return (
            <span key={k} className={`flex items-center gap-1 ${meta.color}`}>
              <Icon size={11} />{meta.label}: <b>{c}</b>
            </span>
          );
        })}
        {Object.keys(groupedByKind).length === 0 && !isLoading && (
          <span className="text-text-faint italic">Aucun event sur cette période. Le réseau est calme.</span>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <table className="luc-table text-xs">
          <thead className="sticky top-0 bg-bg z-10">
            <tr>
              <th>Heure</th>
              <th>Type</th>
              <th>Cible</th>
              <th>Détails</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const meta = KIND_LABELS[a.kind] ?? { label: a.kind, color: 'text-text-muted', icon: AlertTriangle };
              const Icon = meta.icon;
              const details = typeof a.details === 'string' ? (() => { try { return JSON.parse(a.details); } catch { return {}; } })() : (a.details ?? {});
              const ep = (details as any).endpoint_id;
              const name = (details as any).name ?? (details as any).ip;
              return (
                <tr key={a.id} className="hover:bg-bg-2">
                  <td className="font-mono text-text-faint whitespace-nowrap">
                    {new Date(a.at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </td>
                  <td>
                    <span className={`flex items-center gap-1 ${meta.color}`}>
                      <Icon size={11} />{meta.label}
                    </span>
                  </td>
                  <td>
                    {ep
                      ? <Link to={`/app/endpoints/${ep}`} className="hover:text-brand">{name ?? `endpoint #${ep}`}</Link>
                      : <span className="text-text-muted">{name ?? '—'}</span>}
                  </td>
                  <td className="text-text-muted max-w-md truncate" title={JSON.stringify(details)}>
                    {(details as any).days_until_expiry !== undefined && `Expire dans ${(details as any).days_until_expiry}j · `}
                    {(details as any).downtime_sec !== undefined && (details as any).downtime_sec !== null && `Downtime ${Math.round((details as any).downtime_sec / 60)}min · `}
                    {(details as any).error && `Erreur: ${(details as any).error} · `}
                    {(details as any).subject_cn && `CN: ${(details as any).subject_cn}`}
                    {!(details as any).days_until_expiry && !(details as any).downtime_sec && !(details as any).error && !(details as any).subject_cn && (
                      <span className="font-mono text-[10px]">{JSON.stringify(details).slice(0, 120)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={4} className="text-center py-12 text-text-faint italic">Tout est calme ✓</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
